/**
 * Product hero rating pill.
 *
 * Fills the pill that `snippets/product-rating-pill.liquid` renders above the
 * product title, using the public REVIEWS.io product review API (no key). The
 * API answers with a `stats` object holding `average` (a string such as
 * "4.9286") and `count` (a whole number).
 * REVIEWS.io API docs: https://developers.reviews.io/reference/product-reviews
 *
 * The markup ships with the `hidden` attribute. This file removes it only after
 * it has real numbers, so a slow or failed request leaves no empty pill behind.
 */
(function () {
  const API_URL = 'https://api.reviews.io/product/review';
  const AVERAGE_DECIMAL_PLACES = 2;

  /**
   * Read the review stats for one product from REVIEWS.io.
   *
   * @param {string} store - The REVIEWS.io store id.
   * @param {string} sku - Every variant SKU and variant id, joined by `;`.
   * @returns {Promise<{average: number, count: number} | null>} The stats, or null if unavailable.
   */
  const fetchReviewStats = async (store, sku) => {
    const url = `${API_URL}?store=${encodeURIComponent(store)}&sku=${encodeURIComponent(sku)}`;
    const response = await fetch(url);

    // Do not proceed if the request failed. The error body carries no stats.
    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const stats = data && data.stats;

    // Do not proceed if the answer carries no stats. An unknown product still returns 200.
    if (!stats) {
      return null;
    }

    // `average` arrives as a string, so it needs Number(). `count` already is one.
    return { average: Number(stats.average), count: stats.count };
  };

  /**
   * Show the average and the review count inside one pill.
   *
   * Text comes from `window.accessibilityStrings`, so no English lives in this
   * file. Each string holds a slot such as `[count]` that this function swaps
   * for the real value.
   *
   * @param {HTMLElement} pillEl - The pill container.
   * @param {{average: number, count: number}} stats - The review stats.
   * @returns {void}
   */
  const renderPill = (pillEl, stats) => {
    const strings = window.accessibilityStrings;
    const averageText = stats.average.toFixed(AVERAGE_DECIMAL_PLACES);
    const countTemplate =
      stats.count === 1 ? strings.ratingPillCountOne : strings.ratingPillCountOther;
    const countText = countTemplate.replaceAll('[count]', stats.count);

    pillEl.querySelector('[aura-rating-pill-average]').textContent = averageText;
    pillEl.querySelector('[aura-rating-pill-count]').textContent = countText;

    // The star and the dot mean nothing to a screen reader, so give it one
    // sentence instead of parts. Reusing `countText` keeps "1 Review" singular.
    pillEl.querySelector('[aura-rating-pill-label]').textContent = strings.ratingPillLabel
      .replaceAll('[rating]', averageText)
      .replaceAll('[reviews]', countText);

    pillEl.removeAttribute('hidden');
  };

  /**
   * Fetch the stats for one pill and show them.
   *
   * @param {HTMLElement} pillEl - The pill container.
   * @returns {Promise<void>}
   */
  const setUpPill = async (pillEl) => {
    const { store, sku } = pillEl.dataset;

    // Do not proceed without a store and a SKU list. The API would answer for
    // the whole store, not this product.
    if (!store || !sku) {
      return;
    }

    // Do not proceed without the strings.
    if (!window.accessibilityStrings) {
      return;
    }

    // A network error must not break the page. `catch` returns null, so the pill
    // stays hidden and the reviews widget below still works.
    const stats = await fetchReviewStats(store, sku).catch(() => null);

    // Do not proceed without a review count and a usable average. Reviews score
    // from 1 to 5, so a zero or unreadable average means the pill would print a
    // rating the product does not have, such as "0.00" or "NaN".
    if (!stats || !stats.count || !stats.average) {
      return;
    }

    renderPill(pillEl, stats);
  };

  document.querySelectorAll('[aura-rating-pill]').forEach(setUpPill);
})();
